import { Grid } from '@mui/material';
import Spinner from 'components/shared/Spinner';
import React, { useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PaymentMethodType from 'store/enums/PaymentMethodType';
import { PaymentFormValues } from 'store/types/FormValues';
import { ConfigContext } from 'components/ConfigGuard';
import { NmiPayments, PaymentChangeEvent } from '@nmipayments/nmi-pay-react';
import { Appearance } from '@nmipayments/nmi-core';
import PaymentMethodFormProps, { PaymentMethodFormHandle } from 'store/types/payments/PaymentMethodFormProps';

const nmiPaymentFormAppearance: Appearance = {
  theme: 'light',
  customTheme: {
    layout: {
      radius: {
        small: '4px',
        medium: '8px',
        large: '12px',
      },
      borderWidth: {
        small: '1px',
        medium: '1px',
        large: '4px',
      },
      spacing: {
        padding: '16px',
        xs: '4px',
        sm: '8px',
        md: '16px',
        lg: '24px',
        xl: '32px',
        '2xl': '48px',
        '3xl': '64px',
      },
    },
    typography: {
      size: {
        xs: '16px',
        sm: '18px',
        md: '20px',
        lg: '22px',
        xl: '24px',
        '2xl': '26px',
      },
      weight: {
        light: '300',
        medium: '500',
        bold: '700',
      },
      font: {
        sans: 'Work Sans, -apple-system, BlinkMacSystemFont, sans-serif',
        mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      },
    },
  },
  layoutSpacing: 'spacious',
};

const NmiPaymentMethodForm = React.forwardRef<PaymentMethodFormHandle, PaymentMethodFormProps>(
  ({ disabled = false, onMethodValidChange, type = PaymentMethodType.Card, subsidiaryId, onChange }, ref) => {
    const [formValid, setFormValid] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [publicKey, setPublicKey] = useState<string>('');

    const latestPaymentValuesRef = useRef<Partial<PaymentFormValues> | null>(null);

    const { paymentConfiguration } = useContext(ConfigContext);

    useEffect(() => {
      onMethodValidChange(formValid);
    }, [formValid, onMethodValidChange]);

    useEffect(() => {
      const subsidiaryPublicKey = paymentConfiguration?.solupayItems?.find((i) => i.subsidiaryId === subsidiaryId);

      setPublicKey(subsidiaryPublicKey?.publicKey ?? paymentConfiguration?.solupay?.publicKey ?? '');
    }, [subsidiaryId, paymentConfiguration]);

    const paymentFormOnChange = useCallback(
      (data: PaymentChangeEvent) => {
        const complete = data.complete ?? false;

        setFormValid(complete);

        if (!complete) {
          latestPaymentValuesRef.current = null;

          onChange?.({
            paymentMethodValid: false,
            token: undefined,
          });

          return;
        }

        if (data.lookupData?.card || data.lookupData?.check) {
          const values: Partial<PaymentFormValues> = {
            paymentMethodValid: true,
            card: {
              bin: data.lookupData?.card?.bin,
              exp: data.lookupData?.card?.exp,
              hash: data.lookupData?.card?.hash,
              number: data.lookupData?.card?.number,
              type: data.lookupData?.card?.type,
            },
            check: {
              aba: data.lookupData?.check?.aba,
              account: data.lookupData?.check?.account,
              name: data.lookupData?.check?.name,
              hash: data.lookupData?.check?.hash,
            },
            token: data.token,
          };

          latestPaymentValuesRef.current = values;

          /**
           * Keep this for backwards compatibility with existing pages
           * that expect NMI values to arrive through onChange.
           */
          onChange?.(values);
        }
      },
      [onChange]
    );

    const paymentFormOnLoaded = useCallback(() => {
      setLoading(false);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        finalizePaymentMethod: async () => {
          if (disabled) {
            throw new Error('Payment form is disabled.');
          }

          if (!latestPaymentValuesRef.current?.paymentMethodValid) {
            throw new Error('Payment method is incomplete or invalid.');
          }

          const values = latestPaymentValuesRef.current;

          return values;
        },
      }),
      [disabled]
    );

    return (
      <Spinner loading={loading} transparent={false}>
        <Grid>
          {!disabled && (
            <NmiPayments
              tokenizationKey={publicKey}
              paymentMethods={type === PaymentMethodType.Card ? ['card'] : ['ach']}
              appearance={nmiPaymentFormAppearance}
              onChange={paymentFormOnChange}
              onFieldsAvailable={paymentFormOnLoaded}
            />
          )}
        </Grid>
      </Spinner>
    );
  }
);

NmiPaymentMethodForm.displayName = 'NmiPaymentMethodForm';

export default NmiPaymentMethodForm;
